/**
 * FlahaSOIL v2 — USDA texture-triangle SVG visualisation (Phase 10C-D).
 */
import { Box, Stack, Typography, Tooltip } from "@mui/material";
import type { TextureAnalysisBlock } from "@flaha/shared-types";
import {
  DEFAULT_TRIANGLE_VERTICES,
  TEXTURE_CLASSIFICATION_ORDER,
  USDA_TEXTURE_POLYGONS,
  barycentricToCartesian,
} from "@flaha/soil-physics";
import type { ScientificChartData } from "../types/ScientificChartTypes";

import { flahaSoilColors } from "../../../../theme/flahaSoilTheme";

const SIZE = 360;
const PAD = 28;

/** Token-derived triangle chrome (see the results-panel twin). */
const CHART_HIGHLIGHT_FILL = "rgba(111,139,61,0.30)"; // flahaSoilColors.olive500 @ 30%
const CHART_MUTED_FILL = "rgba(36,26,20,0.05)"; // flahaSoilColors.textPrimary @ 5%

function project(sand: number, silt: number, clay: number): { x: number; y: number } {
  const u = barycentricToCartesian({ sand, silt, clay }, DEFAULT_TRIANGLE_VERTICES);
  const scale = SIZE - 2 * PAD;
  return { x: PAD + u.x * scale, y: PAD + u.y * scale };
}

function polygonToPath(name: string): string {
  const poly = USDA_TEXTURE_POLYGONS[name];
  if (!poly || poly.length === 0) return "";
  return (
    poly
      .map((v, i) => {
        const p = project(v.sand, v.silt, v.clay);
        return `${i === 0 ? "M" : "L"} ${p.x.toFixed(2)} ${p.y.toFixed(2)}`;
      })
      .join(" ") + " Z"
  );
}

interface TextureTriangleChartProps {
  chartData: ScientificChartData<TextureAnalysisBlock | null>;
}

export function TextureTriangleChart({ chartData }: TextureTriangleChartProps) {
  const { data: texture, accessibilitySummary } = chartData;
  const clayApex = project(0, 0, 100);
  const sandApex = project(100, 0, 0);
  const siltApex = project(0, 100, 0);
  const point = texture
    ? project(texture.normalized.sand, texture.normalized.silt, texture.normalized.clay)
    : null;

  return (
    <Stack spacing={1.5} aria-label={accessibilitySummary}>
      <Box
        component="svg"
        data-chart-core="textureTriangle"
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        role="img"
        sx={{
          width: "100%",
          height: "auto",
          maxWidth: SIZE,
          backgroundColor: "#FFFFFF",
        }}
      >
        {TEXTURE_CLASSIFICATION_ORDER.map((cls) => (
          <path
            key={cls}
            d={polygonToPath(cls)}
            fill={texture?.classification === cls ? CHART_HIGHLIGHT_FILL : CHART_MUTED_FILL}
            stroke={flahaSoilColors.textMuted}
            strokeWidth={0.75}
          >
            <title>{cls}</title>
          </path>
        ))}
        <text x={clayApex.x} y={clayApex.y - 8} textAnchor="middle" fontSize={14} fontWeight={700} fill={flahaSoilColors.textPrimary}>
          100% Clay
        </text>
        <text x={sandApex.x - 4} y={sandApex.y + 18} textAnchor="end" fontSize={14} fontWeight={700} fill={flahaSoilColors.textPrimary}>
          100% Sand
        </text>
        <text x={siltApex.x + 4} y={siltApex.y + 18} textAnchor="start" fontSize={14} fontWeight={700} fill={flahaSoilColors.textPrimary}>
          100% Silt
        </text>
        {point ? (
          <Tooltip
            title={`${texture?.classification ?? "Unclassified"} — sand ${texture?.normalized.sand.toFixed(1)}%, silt ${texture?.normalized.silt.toFixed(1)}%, clay ${texture?.normalized.clay.toFixed(1)}%`}
            arrow
          >
            <circle
              cx={point.x}
              cy={point.y}
              r={6}
              fill={flahaSoilColors.blue700}
              stroke="#FFFFFF"
              strokeWidth={2}
              data-testid="texture-point"
              style={{ cursor: "help" }}
            />
          </Tooltip>
        ) : null}
      </Box>
      <Typography variant="body2" color="text.secondary" data-print-hide>
        {texture
          ? `Classification: ${texture.classification ?? "Unmatched"} (sand ${texture.normalized.sand.toFixed(1)}% / silt ${texture.normalized.silt.toFixed(1)}% / clay ${texture.normalized.clay.toFixed(1)}%)`
          : "No texture data — enter sand / silt / clay to plot."}
      </Typography>
    </Stack>
  );
}
